/**
 * One failure mode — DR-2026-352 v0.5.0 (D10–D12). The mode's mechanism and signature, its
 * queue rows (D11: read, not recomputed — the act is on /qms/review-queue), every
 * occurrence with its register row, and every control with the weekly audit's existence
 * verdict and its instant (D10: never checked is a state, never a clean).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Header, Segment, Table, Label, Loader, Message } from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';
import {
  SEVERITY_COLOR, MODE_STATUS_COLOR, fmtDate, controlExistence, recurredChip, proposalChip,
} from './failureModeFormat';

const QUEUE_LABEL = { failure_mode_new: 'proposal', failure_mode_recurred_after_control: '5a recurrence' };
const sub = { fontSize: '0.85em', color: '#777' };

export default function FailureModeDetail() {
  const { modeId } = useParams();
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchApi(`${getApiPath()}/@qms/failure-modes/${encodeURIComponent(modeId)}`)
      .then((d) => { setMode(d); setError(null); })
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [modeId]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading failure mode..." />
      </Container>
    );
  }
  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Link to="/qms/failure-modes">&larr; Failure modes</Link>
        <Message negative>{error}</Message>
      </Container>
    );
  }
  if (!mode) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Link to="/qms/failure-modes">&larr; Failure modes</Link>
        <Segment>No such failure mode.</Segment>
      </Container>
    );
  }

  const queue = mode.queue || [];
  const controls = mode.controls || [];
  const occurrences = mode.occurrences || [];

  return (
    <Container style={{ paddingTop: '1rem', paddingBottom: '3em' }}>
      <Helmet title={`${mode.mode_id} — ${mode.name}`} />
      <Link to="/qms/failure-modes">&larr; Failure modes</Link>
      <Header as="h1" style={{ marginTop: '0.5em', marginBottom: '0.25rem' }}>
        <code>{mode.mode_id}</code> {mode.name}
        <Header.Subheader>
          family {mode.family} — {mode.family_label}{mode.capa_id ? ` (${mode.capa_id})` : ' (no CAPA)'}
          {' · first seen '}{fmtDate(mode.first_seen_at)}
        </Header.Subheader>
      </Header>
      <div style={{ marginBottom: '1rem' }}>
        <Label color={SEVERITY_COLOR[mode.severity_class]}>{mode.severity_class}</Label>
        <Label color={MODE_STATUS_COLOR[mode.status]}>
          {mode.status}{mode.merged_into ? ` → ${mode.merged_into}` : ''}
        </Label>
        {(mode.merged_from || []).map((m) => (
          <Label key={m.mode_id} basic>absorbs {m.mode_id}</Label>
        ))}
      </div>

      <Segment>
        <p><strong>Mechanism.</strong> {mode.description}</p>
        <p><strong>Signature.</strong> {mode.signature}</p>
      </Segment>

      <Header as="h3">Queue</Header>
      {queue.length ? (
        <Table compact="very" size="small" basic="very">
          <Table.Body>
            {queue.map((r) => {
              const chip = r.finding_type === 'failure_mode_new' ? proposalChip(r) : recurredChip(r);
              return (
                <Table.Row key={r.id}>
                  <Table.Cell width={3}>
                    <Label size="small" color={chip ? chip.color : 'grey'}>
                      {chip ? chip.text : QUEUE_LABEL[r.finding_type]}
                    </Label>
                    <div style={{ fontSize: '0.8em', color: '#777' }}>
                      {r.status}{r.disposition ? ` · ${r.disposition} by ${r.disposition_by}` : ''}
                    </div>
                  </Table.Cell>
                  <Table.Cell width={10} style={{ color: '#333' }}>
                    {r.detail}
                    {r.recommendation_basis ? (
                      <div style={{ fontSize: '0.85em', color: '#555', marginTop: '0.3rem' }}>
                        Recommendation {r.recommendation}: {r.recommendation_basis}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell width={3}>
                    <Link to={`/qms/review-queue?type=${r.finding_type}&class=all`}>decide on the queue &rarr;</Link>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : <p style={{ color: '#777' }}>No queue row for this mode.</p>}

      <Header as="h3">Controls ({controls.length})</Header>
      {controls.length ? (
        <Table compact="very" size="small" basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Artifact</Table.HeaderCell>
              <Table.HeaderCell>Kind</Table.HeaderCell>
              <Table.HeaderCell>Shipped</Table.HeaderCell>
              <Table.HeaderCell>Exists</Table.HeaderCell>
              <Table.HeaderCell>Notes</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {controls.map((c) => {
              const ex = controlExistence(c);
              return (
                <Table.Row key={c.id} disabled={!!c.retired_at}>
                  <Table.Cell><code>{c.artifact}</code></Table.Cell>
                  <Table.Cell>{c.kind}</Table.Cell>
                  <Table.Cell>
                    {fmtDate(c.shipped_at)}
                    {c.shipped_by ? <div style={sub}>{c.shipped_by}</div> : null}
                    {c.retired_at ? <div style={{ fontSize: '0.8em', color: '#a33' }}>retired {fmtDate(c.retired_at)}</div> : null}
                  </Table.Cell>
                  <Table.Cell><Label size="small" basic color={ex.color}>{ex.text}</Label></Table.Cell>
                  <Table.Cell style={{ fontSize: '0.85em', color: '#555' }}>{c.notes}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : (
        <p style={{ color: '#a33' }}>No control row — nothing is declared to catch the next occurrence.</p>
      )}

      <Header as="h3">Occurrences ({occurrences.length})</Header>
      {occurrences.length ? (
        <Table compact="very" size="small" basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Report</Table.HeaderCell>
              <Table.HeaderCell>Occurred</Table.HeaderCell>
              <Table.HeaderCell>Caught by</Table.HeaderCell>
              <Table.HeaderCell>Consequence</Table.HeaderCell>
              <Table.HeaderCell>Cost</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {occurrences.map((o) => (
              <Table.Row key={o.id}>
                <Table.Cell>
                  <Link to={`/qms/issues/${encodeURIComponent(o.report_id)}`}><code>{o.report_id}</code></Link>
                  <div style={{ fontSize: '0.85em', color: '#555' }}>{o.report_title}</div>
                  <Label size="mini" horizontal>{o.report_status}</Label>
                </Table.Cell>
                <Table.Cell>
                  {fmtDate(o.occurred_at)}
                  {o.session_number ? <div style={sub}>s{o.session_number}</div> : null}
                </Table.Cell>
                <Table.Cell>{o.caught_by_label}</Table.Cell>
                <Table.Cell style={{ color: '#333' }}>
                  {o.consequence}
                  {o.notes ? <div style={sub}>{o.notes}</div> : null}
                </Table.Cell>
                <Table.Cell>
                  {o.cost_minutes == null && o.exposure_days == null ? '—' : null}
                  {o.cost_minutes != null ? `${o.cost_minutes} min` : ''}
                  {o.cost_minutes != null && o.exposure_days != null ? ' · ' : ''}
                  {o.exposure_days != null ? `${o.exposure_days} d` : ''}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      ) : <p style={{ color: '#777' }}>No occurrence on the register.</p>}
    </Container>
  );
}
